import React, { useState } from "react";
import { validateUrl, normalizeUrl } from "../../../lib/linkUrl";

export interface LinkFormProps {
  /** Pre-fills the URL field when the user opened the form to edit a link. */
  initialUrl?: string;
  /** Pre-fills the title field when the user opened the form to edit a link. */
  initialDescription?: string;
  /** Label on the confirm button — "Add Link" or "Save Link". */
  submitLabel: string;
  /** Accessible name for the cancel button, which carries no visible label. */
  cancelAriaLabel: string;
  /**
   * Called with a normalized URL and a trimmed title once both fields are
   * filled and the URL validates. Persisting the link, and closing this form,
   * are the caller's job.
   */
  onSubmit: (url: string, description: string) => void;
  onCancel: () => void;
}

/**
 * The add/edit form for a link, shared by the task and sub-task surfaces of the
 * task dialog. Owns the two fields and the URL validation error; the caller
 * decides what a submitted link means and when the form closes.
 *
 * Callers that reuse a mounted form for a different link should give it a `key`
 * so the initial values are picked up.
 */
const LinkForm: React.FC<LinkFormProps> = ({
  initialUrl = "",
  initialDescription = "",
  submitLabel,
  cancelAriaLabel,
  onSubmit,
  onCancel,
}) => {
  const [url, setUrl] = useState(initialUrl);
  const [description, setDescription] = useState(initialDescription);
  const [urlError, setUrlError] = useState("");

  const submit = () => {
    if (!url.trim() || !description.trim()) return;

    const error = validateUrl(url);
    if (error) {
      setUrlError(error);
      return;
    }
    setUrlError("");

    onSubmit(normalizeUrl(url), description.trim());
  };

  return (
    <div className="space-y-2 p-3 bg-warm-bg-elevated rounded-soft border border-warm-border">
      <input
        type="text"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Link title"
        className="w-full px-3 py-2 text-sm border border-warm-border rounded-soft bg-warm-bg-card text-warm-text-primary placeholder-warm-text-tertiary focus:outline-none focus:ring-2 focus:ring-warm-blue"
        autoFocus
      />
      <input
        type="text"
        value={url}
        onChange={(e) => {
          setUrl(e.target.value);
          setUrlError("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="URL (e.g., example.com or https://example.com)"
        className={`w-full px-3 py-2 text-sm border rounded-soft bg-warm-bg-card text-warm-text-primary placeholder-warm-text-tertiary focus:outline-none focus:ring-2 ${
          urlError
            ? "border-red-500 focus:ring-red-500 dark:border-red-500"
            : "border-warm-border focus:ring-warm-blue"
        }`}
      />
      {urlError && (
        <p className="text-sm text-red-600 dark:text-red-400">{urlError}</p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={!url.trim() || !description.trim()}
          className="flex-1 px-3 py-2 text-sm bg-warm-blue hover:bg-warm-blue-dark disabled:bg-warm-bg-elevated disabled:cursor-not-allowed text-white rounded-soft transition-colors"
        >
          {submitLabel}
        </button>
        <button
          type="button"
          aria-label={cancelAriaLabel}
          onClick={onCancel}
          className="px-3 py-2 text-sm bg-warm-bg-elevated hover:bg-warm-border-subtle text-warm-text-secondary rounded-soft transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default LinkForm;
